<template>
  <v-card>
    <v-card-title>
      <v-icon left>mdi-image</v-icon>
      Logo de la Empresa
    </v-card-title>
    
    <v-card-text>
      <!-- Preview del logo actual -->
      <div v-if="logoPreview" class="mb-4 text-center">
        <v-img
          :src="logoPreview"
          max-width="300"
          max-height="150"
          contain
          class="mx-auto elevation-2 logo-preview"
          @error="onImageError"
        />
      </div>
      
      <div v-else class="mb-4 text-center pa-4">
        <v-icon size="64" color="grey lighten-1">mdi-image-off</v-icon>
        <p class="grey--text mt-2">No hay logo cargado</p>
      </div>
      
      <!-- Upload -->
      <v-file-input
        v-model="logoFile"
        label="Seleccionar logo (PNG)"
        accept="image/png"
        prepend-icon="mdi-camera"
        show-size
        @change="onFileChange"
      >
        <template v-slot:selection="{ text }">
          <v-chip small label color="primary">
            {{ text }}
          </v-chip>
        </template>
      </v-file-input>
      
      <v-alert v-if="error" type="error" dense class="mt-2">
        {{ error }}
      </v-alert>
      
      <v-alert type="info" dense text class="mt-2">
        <strong>Requisitos:</strong>
        <ul class="mb-0">
          <li>Formato: PNG únicamente</li>
          <li>Tamaño máximo: 5MB</li>
          <li>Recomendado: 600x200px con fondo transparente</li>
        </ul>
      </v-alert>
      
      <v-alert type="success" dense text class="mt-2" v-if="empresaSeleccionada">
        <strong>💡 Nota:</strong> Al subir el logo, se configurará automáticamente en el template activo y podrás verlo en la vista previa del remito.
      </v-alert>
    </v-card-text>
    
    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="!logoFile || uploading"
        :loading="uploading"
        @click="uploadLogo"
      >
        <v-icon left>mdi-upload</v-icon>
        Subir Logo
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import api from "../../services/api";

export default {
  name: 'LogoUploader',
  
  props: {
    idEmpresa: {
      type: Number,
      required: true
    }
  },
  
  computed: {
    empresaSeleccionada() {
      return this.idEmpresa
    }
  },
  
  data() {
    return {
      logoFile: null,
      logoPreview: null,
      uploading: false,
      error: null,
      imageError: false
    }
  },
  
  watch: {
    idEmpresa: {
      immediate: true,
      handler() {
        this.loadCurrentLogo()
      }
    }
  },
  
  methods: {
    loadCurrentLogo() {
      if (!this.idEmpresa) return
      
      // Cargar logo actual si existe (agregar timestamp para evitar cache)
      this.imageError = false
      const baseUrl = api.defaults.baseURL || window.location.origin + '/apiv3';
      const originUrl = baseUrl.replace('/apiv3', '');
      this.logoPreview = `${originUrl}/uploads/logos/${this.idEmpresa}/logo.png?t=${Date.now()}`
    },
    
    onImageError() {
      // Si la imagen no existe, ocultar el preview
      this.imageError = true
      this.logoPreview = null
    },
    
    onFileChange(file) {
      this.error = null
      
      if (!file) {
        this.logoPreview = null
        return
      }
      
      // Validar tipo
      if (file.type !== 'image/png') {
        this.error = 'Solo se permiten archivos PNG'
        this.logoFile = null
        return
      }
      
      // Validar tamaño (5MB)
      if (file.size > 5000000) {
        this.error = 'El archivo debe ser menor a 5MB'
        this.logoFile = null
        return
      }
      
      // Generar preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.logoPreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    
    async uploadLogo() {
      if (!this.logoFile) return
      
      this.uploading = true
      this.error = null
      
      try {
        const result = await this.$store.dispatch('remitoTemplates/uploadLogo', {
          idEmpresa: this.idEmpresa,
          file: this.logoFile
        })
        
        this.$store.commit('ui/SHOW_NOTIFICATION', {
          text: 'Logo subido correctamente y se usará en los remitos',
          type: 'success'
        })
        
        // Esperar un momento antes de recargar para asegurar que el archivo esté disponible
        setTimeout(() => {
          this.loadCurrentLogo()
          this.logoFile = null
          
          // Emitir evento para que el padre pueda actualizar
          this.$emit('logo-uploaded', result)
        }, 500)
        
      } catch (error) {
        console.error('[LogoUploader] Error:', error)
        this.error = error.message || 'Error al subir el logo'
        
        this.$store.commit('ui/SHOW_NOTIFICATION', {
          text: this.error,
          type: 'error'
        })
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.logo-preview {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
  padding: 16px;
}

.theme--dark .logo-preview {
  background-color: #1e1e1e;
  border-color: #424242;
}
</style>
